<template>
    <div class="rounded-lg pt-2 flex items-center justify-center">
        <div v-if="chartVisibility" class="w-full flex flex-col items-center pt-2 mb-10">
            <highcharts
                ref="funnel-chart"
                class="chart-content_wrapper"
                :options="chartOptions"
                :callback="onInitChartCallback"
                :updateArgs="[true, true]"
            />
            <div class="my-4 w-full max-w-screen-md" v-if="data.WidgetLayout.showAutoDialerTable">
                <data-table
                :border="tableBorder"
                :columns="availableColumns"
                :editable="editable"
                can-sort-rows="custom"
                :showColumns="visibleColumns"
                :tableData="fetchTableData"
                class="w-full"
                @sort-change="onSortChange"
            >
            </data-table>
            </div>
        </div>
        <div v-else class="flex flex-col icon-no-data-funnel items-center">
            <icon-no-data-funnel />
        </div>
    </div>
</template>
<script>
    import Highcharts from 'highcharts'
    import { Chart } from 'highcharts-vue'
    import Funnel from 'highcharts/modules/funnel'
    import actionMixin from '@/components/Charts/Configs/actionMixin'
    import dialerMixin from '@/mixins/dialerMixin' 
    import get from 'lodash/get'
    
    Funnel(Highcharts)

    const tableStatusesKeys = ['legOneRinging', 'legTwoRinging', 'talking']
    const tableKeys = ['#', 'campaignName', ...tableStatusesKeys]
    const tableKeysForGettingData = {
        '#': 'campaignID',
        'campaignName': 'name',
        'legOneRinging': 'data.CallStatistics.TotalLeg1RingingCalls',
        'legTwoRinging': 'data.CallStatistics.TotalLeg2RingingCalls',
        'talking': 'data.CallStatistics.TotalLeg2AnswerCalls'
    }
    
    export default {
        mixins: [actionMixin, dialerMixin],
        components: {
            Highcharts,
            highcharts: Chart,
            DataTable: () => import('@/components/Table/DataTableWithoutResizeColumn')
        },
        props: {
            data: {
                type: Object,
                default: () => ({}),
            },
            editable: {
                type: Boolean,
                default: true,
            },
        },
        data () {
            return {
                chartVisibility: false,
                chartOptions: {},
                chartInstance: false,
                tableBorder: true
            }
        },
        computed: {
            getAllDialersToDisplay () {
                return this.getAllDialersWithTypeIVR
            },
            getAllDialersToDisplayByCampaigns () {
                const campaigns = this.campaigns
                if (!campaigns) {
                    return
                }
                return this.getAllDialersToDisplay
                    .filter(el => el.data && Object.keys(el.data).length && campaigns.includes(el.campaignID))
                    .sort((a, b) => a.name !== b.name ? a.name < b.name ? -1 : 1 : 0)
            },
            availableColumns () {
                let columns = []
                if (this.displayTableAsRow) {
                    const allDialers = this.getAllDialersToDisplayByCampaigns
                    if (!allDialers) {
                        return
                    }
                    const allDialerColumns = allDialers.map(el => {
                        return {
                            prop: el.name,
                            fixed: false,
                            align: 'center',
                            label: el.name
                        }
                    })
                    const additionCol = {
                        prop: 'additionCol',
                        fixed: false,
                        align: 'center'
                    }

                    columns = [additionCol, ...allDialerColumns]
                } else {
                    columns = tableKeys
                        .map(el => {
                            return {
                                prop: el,
                                fixed: false,
                                align: 'center',
                                label: el,
                                sortable: true,
                                minWidth: el === '#' ? 40 : 100
                            }
                        })
                }

                return columns
            },
            visibleColumns () {
                let columns = []
                if (this.displayTableAsRow) {
                    const allDialers = this.getAllDialersToDisplayByCampaigns
                    if (!allDialers) {
                        return
                    }
                    const allDialerNames = allDialers.map(el => el.name)
                    columns = ['additionCol', ...allDialerNames]
                } else {
                    columns = tableKeys
                }

                return columns
            },
            fetchTableData: {
                get: function () {
                    const data = []
                    const makeDynamicRowWithValue = (value) => {
                        const row = {}
                        const arrayForIteration = this.displayTableAsRow ? this.getAllDialersToDisplayByCampaigns : tableKeys

                        arrayForIteration.forEach(el => {
                            if (this.displayTableAsRow) {
                                row[el.name] = get(el, tableKeysForGettingData[value], '')
                            } else {
                                row[el] = get(value, tableKeysForGettingData[el], '')
                            }
                        })

                        return row
                    }

                    if (this.displayTableAsRow) {
                        tableStatusesKeys.forEach(key => {
                            const firstColWithTranslation = this.$t(`widget.table.header.title.full.${key}`)
                            data.push({
                                additionCol: firstColWithTranslation,
                                ...makeDynamicRowWithValue(key)
                            })
                        })

                    } else {
                        this.getAllDialersToDisplayByCampaigns.forEach(el => {
                            data.push(
                                makeDynamicRowWithValue(el)    
                            )
                        })
                    }

                    return data
                },
                set: function (newValue) {
                    return newValue
                }
            },
            campaigns () {
                const campaignsList = this.data.WidgetConfig.find(el => el.ParameterName === '{|campaign_ivr_list|}')
                if (!campaignsList) {
                    return
                }

                return campaignsList.WidgetParameterValueJson.EntityPositive
            },
            displayTableAsRow() {
                return this.data.WidgetLayout.displayTableAsRow
            }
        },
        methods: {
            onInitChartCallback(chart) {
                this.chartInstance = chart
            },
            tryPrintChart() {
                const divToPrint = this.$el.children[0]
                const newWin = window.open();
                newWin.document.write(divToPrint.innerHTML);
                newWin.document.close();
                newWin.focus();
                newWin.print();
            },
            tryDownloadChart(type) {
                if (!this.$refs['funnel-chart']) {
                    this.$notify({
                        type: 'primary',
                        icon: true,
                        title: this.$t('dialer.no.calls')
                    })
                    return
                }
                this.$refs['funnel-chart'].chart.exportChart({
                    type: type
                })
            },
            async getWidgetData() {
                const campaigns = this.campaigns
                if (!campaigns) {
                    return
                }
                let allDialersWithData = this.getAllDialersToDisplay
                    .filter(el => el.data && Object.keys(el.data).length && campaigns.includes(el.campaignID))

                if (!allDialersWithData || allDialersWithData.length === 0) {
                    this.chartVisibility = false
                    return
                }

                const copyOfAllDialersWithData = JSON.parse(JSON.stringify(allDialersWithData))
                const getAllCampaignNamesByStatus = (key) => {
                    const getAllCampaignNamesWithData = copyOfAllDialersWithData
                        .map(el => {
                            if (el.data.CallStatistics[key] > 0) {
                                return el.name
                            }
                            return ''

                        })
                        .filter(el => el)

                    return getAllCampaignNamesWithData
                }

                allDialersWithData = allDialersWithData
                    .map(el => {
                        return {
                            CallStatistics: el.data.CallStatistics
                        }
                    })
                    .reduce((previousValue, currentValue) => {
                        return {
                            TotalCalls: previousValue.CallStatistics.TotalCalls + currentValue.CallStatistics.TotalCalls,
                            TotalLeg1AnswerCalls: previousValue.CallStatistics.TotalLeg1AnswerCalls + currentValue.CallStatistics.TotalLeg1AnswerCalls,
                            TotalLeg1RingingCalls: previousValue.CallStatistics.TotalLeg1RingingCalls + currentValue.CallStatistics.TotalLeg1RingingCalls,
                            TotalLeg2AnswerCalls: previousValue.CallStatistics.TotalLeg2AnswerCalls + currentValue.CallStatistics.TotalLeg2AnswerCalls,
                            TotalLeg2RingingCalls: previousValue.CallStatistics.TotalLeg2RingingCalls + currentValue.CallStatistics.TotalLeg2RingingCalls,
                            TotalOriginatingCalls: previousValue.CallStatistics.TotalOriginatingCalls + currentValue.CallStatistics.TotalOriginatingCalls,
                            TotalPendingCalls: previousValue.CallStatistics.TotalPendingCalls + currentValue.CallStatistics.TotalPendingCalls
                        }
                    })

                const hasKeyCallStatistics = 'CallStatistics' in allDialersWithData
                if ((!hasKeyCallStatistics && Object.values(allDialersWithData).every(el => el === 0))
                    || (hasKeyCallStatistics && Object.values(allDialersWithData.CallStatistics).every(el => el === 0)))
                {
                    this.chartVisibility = false
                    return
                }

                const dataChart = hasKeyCallStatistics ? allDialersWithData.CallStatistics : allDialersWithData

                const data = {
                    series: [{
                        name: this.$t('dialer.totals'),
                        data: [
                            [this.$t('dialer.totalPendingCalls'), dataChart.TotalPendingCalls],
                            [this.$t('dialer.totalOriginatingCalls'), dataChart.TotalOriginatingCalls],
                            [this.$t('dialer.totalLeg1RingingCalls'), dataChart.TotalLeg1RingingCalls],
                            [this.$t('dialer.totalLeg1AnswerCalls'), dataChart.TotalLeg1AnswerCalls],
                            [this.$t('dialer.totalLeg2RingingCalls'), dataChart.TotalLeg2RingingCalls],
                            [this.$t('dialer.totalLeg2AnswerCalls'), dataChart.TotalLeg2AnswerCalls]
                        ],
                        showInLegend: true
                    }]
                }
                
                this.chartOptions = {
                    ...data,
                    chart: {
                        type: 'funnel',
                        backgroundColor: 'transparent'
                    },
                    title: {
                        text: ''
                    },
                    plotOptions: {
                        series: {
                            dataLabels: {
                                enabled: true,
                                format: '<b>{point.name}</b> ({point.y:,.0f})',
                                softConnector: true
                            },
                            center: ['40%', '50%'],
                            neckWidth: '30%',
                            neckHeight: '25%',
                            width: '70%'
                        }
                    },
                    legend: {
                        enabled: false
                    },
                    tooltip: {
                        formatter: function() {
                            return getAllCampaignNamesByStatus(this.key).toString()
                        }
                    },
                    responsive: {
                        rules: [{
                            condition: {
                                maxWidth: 500
                            },
                            chartOptions: {
                                plotOptions: {
                                    series: {
                                        dataLabels: {
                                            inside: true
                                        },
                                        center: ['50%', '50%'],
                                        width: '80%'
                                    }
                                }
                            }
                        }]
                    }
                }
                this.reDrawChart()
            },
            reDrawChart() {
                this.chartVisibility = false
                this.$nextTick(() => {
                    this.chartVisibility = true
                })
            },
            onSortChange({column, prop, order}) {
                if (prop === null) {
                    return
                }

                if (order === 'ascending') {
                    this.fetchTableData = this.fetchTableData.sort((a, b) => (a[prop] > b[prop]) ? 1 : -1)
                } else if (order === 'descending') {
                    this.fetchTableData = this.fetchTableData.sort((a, b) => (a[prop] < b[prop]) ? 1 : -1)
                }
            }
        },
        mounted() {
            this.getWidgetData()
            if (this.data.WidgetLayout && !('showAutoDialerTable' in this.data.WidgetLayout)) {
                this.$set(this.data.WidgetLayout, 'showAutoDialerTable', true)
            }
            if (this.data.WidgetLayout && !('displayTableAsRow' in this.data.WidgetLayout)) {
                this.$set(this.data.WidgetLayout, 'displayTableAsRow', false)
            }
        },
        watch: {
            getAllDialersToDisplay: {
                immediate: true,
                handler: function () {
                    this.getWidgetData()
                }
            },
            data: {
                immediate: true,
                handler: function () {
                    this.getWidgetData()
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
::v-deep .highcharts-credits {
    display: none;
}
::v-deep .el-table td > .cell {
    line-height: 1.5 !important
}
.icon-no-data-funnel {
    max-width: 706px;
    width: 100%;
}
</style>
